/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { addThrottledEvent, Node } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { RichTextComponent } from '@msdyn365-commerce/core';
import AccordionItem from 'mfcl/AccordionItem';
import * as React from 'react';
import _getViewPort from '../../Utilities/get-view-port';

import { IMfrmProductDetailsData } from './mfrm-product-details.data';
import { IMfrmProductDetailsProps } from './mfrm-product-details.props.autogenerated';
import { MfrmProductDetailsSpecification } from './mfrm-product-details-specification/mfrm-product-details-specification';
export interface IPanelContent {
    header: string;
    content: React.ReactElement;
    id: string;
}
export interface IResponsiveState {
    isMobile: boolean;
    isTablet: boolean;
    activeTab: number;
}

export interface IResultData {
    AttributeValueId?: number;
    DataTypeValue?: number;
    ExtensionProperties?: [];
    Name?: string;
    RecordId?: number;
    TextValue?: string;
}

export interface IMfrmProductDetailsViewProps extends IMfrmProductDetailsProps<IMfrmProductDetailsData> {
    accordionData: React.ReactNode;
    panelContent: [];
}

/**
 *
 * MfrmProductDetails component
 * @extends {React.PureComponent<IMfrmProductDetailsProps<IMfrmProductDetailsData>>}
 */
class MfrmProductDetails extends React.PureComponent<IMfrmProductDetailsProps<IMfrmProductDetailsData>, IResponsiveState> {
    private panelContent: IPanelContent[];

    private resizeThrottledEventHandler?: (event: Event) => void;
    constructor(props: IMfrmProductDetailsProps<IMfrmProductDetailsData>) {
        super(props);
        this.panelContent = [];
        this.state = {
            isMobile: false,
            isTablet: false,
            activeTab: 0
        };
    }
    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            this.resizeThrottledEventHandler = addThrottledEvent(window, 'resize', () => this._updateViewport());
        }
        this._updateViewport();
    }

    public componentWillUnmount(): void {
        MsDyn365.isBrowser && window.removeEventListener('resize', this.resizeThrottledEventHandler!, false);
    }
    public render(): JSX.Element | null {
        const { descriptionHeading, specificationsHeading, aboutHeading, deliveryShippingHeading, warrantyHeading } = this.props.resources;
        this.panelContent = [
            {
                header: descriptionHeading,
                content: this._getDescriptionContent(this.props.slots?.descriptionContent),
                id: descriptionHeading
            },
            {
                header: specificationsHeading,
                content: this._getSpecificationContent(),
                id: specificationsHeading
            },
            {
                header: `${aboutHeading} ${this._getBrandName()}`,
                content: this._getAboutContent(),
                id: aboutHeading
            },
            {
                header: deliveryShippingHeading,
                content: this._getDeliveryShippingContent(this.props.slots?.deliveryShippingContent),
                id: deliveryShippingHeading
            },
            {
                header: warrantyHeading,
                content: this._getWarrentyContent(this.props.slots?.warrantyContent),
                id: warrantyHeading
            }
        ];
        const viewProps = {
            ...this.props,
            accordionData: this._showAccordion(),
            panelContent: this.panelContent
        };
        return this.props.renderView(viewProps);
    }
    private _updateViewport = () => {
        const { context } = this.props;
        const viewPort = _getViewPort(context);
        viewPort === 'xs' || viewPort === 'sm' ? this.setState({ isMobile: true }) : this.setState({ isMobile: false });
        viewPort === 'md' || viewPort === 'lg' ? this.setState({ isTablet: true }) : this.setState({ isTablet: false });
    };
    private _getDescriptionContent(description: React.ReactNode[]): JSX.Element {
        if (this.props.slots && this.props.slots.descriptionContent && this.props.slots.descriptionContent.length) {
            return (
                <>
                    {description && description.length
                        ? description.map((children: React.ReactNode, index: number) => {
                              return <div key={index}>{children}</div>;
                          })
                        : null}
                </>
            );
        }
        return this._defaultContent();
    }

    private _validateResult(key: string): IResultData | undefined {
        const {
            data: {
                productSpecificationData: { result }
            }
        } = this.props;
        const content: object | undefined = result ? result.find(obj => obj?.Name === key) : undefined;
        return content ? content : undefined;
    }

    private _getSpecificationContent(): JSX.Element {
        const result: IResultData | undefined = this._validateResult('Specification');
        if (result?.TextValue) {
            try {
                const props = JSON.parse(result.TextValue);
                return <MfrmProductDetailsSpecification {...props} />;
            } catch (e) {
                return <RichTextComponent className='mfrm-tab-accordion-specs-tab' text={result.TextValue} />;
            }
        }
        return this._defaultContent();
    }

    private _getBrandName(): string {
        const brandKey = this.props.context.app.config?.brandBackofficeAttributePdp;
        const result: IResultData | undefined = this._validateResult(brandKey);
        return result && result.TextValue ? result.TextValue.toLowerCase() : '';
    }

    private _getAboutContent(): JSX.Element {
        const result: IResultData | undefined = this._validateResult('About Brand');
        if (result) {
            return <RichTextComponent className='mfrm-tab-accordion__about-tab' text={result.TextValue} />;
        }
        return this._defaultContent();
    }

    private _getDeliveryShippingContent(deliveryShippingContent: React.ReactNode[]): JSX.Element {
        if (this.props.slots && this.props.slots.deliveryShippingContent && this.props.slots.deliveryShippingContent.length) {
            return (
                <div className='mfrm-tab-accordion__delivery-tab'>
                    {deliveryShippingContent && deliveryShippingContent.length
                        ? deliveryShippingContent.map((children: React.ReactNode, index: number) => {
                              return <React.Fragment key={index}>{children}</React.Fragment>;
                          })
                        : null}
                </div>
            );
        }
        return this._defaultContent();
    }

    private _getWarrentyContent(warrantyContent: React.ReactNode[]): JSX.Element {
        if (this.props.slots && this.props.slots.warrantyContent && this.props.slots.warrantyContent.length) {
            return (
                <>
                    {warrantyContent && warrantyContent.length
                        ? warrantyContent.map((children: React.ReactNode, index: number) => {
                              return <React.Fragment key={index}>{children}</React.Fragment>;
                          })
                        : null}
                </>
            );
        }
        return this._defaultContent();
    }

    private _showAccordion = () => {
        const { isMobile, isTablet } = this.state;
        const accordionContent = isMobile || isTablet ? this.panelContent.slice(2) : '';
        return (
            <div className='mfrm-accordion'>
                {accordionContent &&
                    accordionContent.length &&
                    accordionContent.map((children: IPanelContent, index: number) => {
                        return (
                            <AccordionItem
                                className='mfrm-accordion__item'
                                key={index}
                                title={children.header}
                                id={children.id}
                                content={children.content}
                            />
                        );
                    })}
            </div>
        );
    };

    private _defaultContent(): JSX.Element {
        const { defaultContent } = this.props.resources;
        return <Node className='mfrm-tab-accordion__no-content'>{defaultContent}</Node>;
    }
}

export default MfrmProductDetails;
